import { useState } from 'react';
import {
  BREAKPOINTS_WIDTHS,
  formatMoney,
  template,
  useWindowSize,
} from '@belong/common';
import { Button, Divider, Money, Text } from '@belong/ui';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { PaygPaymentMethodRow } from '../components/payg-payment-method-row';
import { PAY_AS_YOU_GO_STRINGS } from '../strings';
import { usePaygAmounts } from '../use-payg-amounts';
import { usePaygPaymentMethod } from '../use-payg-payment-method';

const STRINGS = PAY_AS_YOU_GO_STRINGS.confirmStep;

type Props = {
  isPaymentUnconfirmed?: boolean;
  onConfirm: () => void;
};

export function PaygConfirmStep({ isPaymentUnconfirmed, onConfirm }: Props) {
  const { total, upfront, remaining, fee, totalDueNow, hasAmounts } =
    usePaygAmounts();
  const paymentMethod = usePaygPaymentMethod();
  const { width } = useWindowSize();
  const [isChangingMethod, setIsChangingMethod] = useState(false);

  const rowVariant = width <= BREAKPOINTS_WIDTHS.SM ? 'p1' : 'body';

  return (
    <div className="flex w-full flex-col items-center pt-2sm sm:pt-2xl">
      <div className="flex w-full flex-col items-center gap-xl sm:gap-2xl md:px-xl">
        <div className="flex flex-col items-center gap-xs text-center sm:max-w-[340px] md:max-w-[700px]">
          <Text fontWeight="semibold" variant="h2">
            {STRINGS.title}
          </Text>
          <Text variant={rowVariant}>{STRINGS.subtitle}</Text>
        </div>

        <div className="flex w-full flex-col gap-2sm">
          <div className="flex flex-col gap-xs">
            <Text fontWeight="semibold">{STRINGS.dueNowTitle}</Text>
            <div className="flex justify-between gap-sm">
              <Text variant={rowVariant}>{STRINGS.upfrontLabel}</Text>
              <Money
                format="DOLLARS_WITH_CENTS"
                fontWeight="semibold"
                value={upfront}
                variant={rowVariant}
              />
            </div>
            {fee > 0 && (
              <div className="flex justify-between gap-sm">
                <Text variant={rowVariant}>{STRINGS.cardFeeLabel}</Text>
                <Money
                  format="DOLLARS_WITH_CENTS"
                  fontWeight="semibold"
                  value={fee}
                  variant={rowVariant}
                />
              </div>
            )}
          </div>
          <Divider borderStyle="dashed" />
          <div className="flex justify-between gap-sm">
            <Text fontWeight="semibold" variant={rowVariant}>
              {STRINGS.totalDueNowLabel}
            </Text>
            <Money
              format="DOLLARS_WITH_CENTS"
              fontWeight="semibold"
              value={totalDueNow}
              variant={rowVariant}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-xs rounded border border-solid border-green bg-green-translucent-light p-sm">
          <Text fontWeight="semibold">{STRINGS.perTaskTitle}</Text>
          <div className="flex justify-between gap-sm">
            <Text>{STRINGS.remainingLabel}</Text>
            <Money
              format="DOLLARS_WITH_CENTS"
              fontWeight="semibold"
              value={remaining}
            />
          </div>
          <Text variant="p1">
            {template(STRINGS.neverExceedNote, {
              total: formatMoney(total, 'DOLLARS_WITH_CENTS'),
            })}
            {fee > 0 && ` ${STRINGS.cardFeeNote}`}
          </Text>
        </div>

        {paymentMethod && (
          <div className="flex w-full flex-col gap-2sm">
            <Text fontWeight="semibold" variant="h3">
              {STRINGS.paymentMethodTitle}
            </Text>
            <PaygPaymentMethodRow
              onChangingMethod={setIsChangingMethod}
              paymentMethod={paymentMethod}
            />
          </div>
        )}
      </div>

      <div className="mt-xl w-full pb-xl sm:mt-2xl md:pb-2xl">
        {isPaymentUnconfirmed && (
          <Text className="mb-sm text-center" variant="p1">
            {PAY_AS_YOU_GO_STRINGS.unconfirmedNote}
          </Text>
        )}
        <QuestionsOrConcernsModalFooter bareOnDesktop>
          <Button
            className="md:w-[260px]"
            disabled={
              !paymentMethod ||
              !hasAmounts ||
              isChangingMethod ||
              isPaymentUnconfirmed
            }
            onClick={onConfirm}
            size={{ _: 'fluid' }}
          >
            {template(STRINGS.cta, {
              amount: formatMoney(totalDueNow, 'DOLLARS_WITH_CENTS'),
            })}
          </Button>
        </QuestionsOrConcernsModalFooter>
      </div>
    </div>
  );
}
